import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api, setSessionToken } from '../api';
import { toast } from 'sonner';
import {
  CheckCircle, AlertCircle, Clock, ChevronRight, MessageCircle, Calendar, User,
  Plane, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import PageLayout from '../components/PageLayout';
import { useSiteSettings } from '../SiteSettingsContext';

const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { licenseRequiredRoles, donationUrl, donationNote } = useSiteSettings();
  const [trips, setTrips] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [waiverStatus, setWaiverStatus] = useState({ signed: false });
  const [licenses, setLicenses] = useState([]);
  const [tripTasks, setTripTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [reportingDonation, setReportingDonation] = useState(false);
  const [selectedTripIdx, setSelectedTripIdx] = useState(0);

  // Check if this is a guardian session
  const [isGuardianSession, setIsGuardianSession] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [dependentNames, setDependentNames] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripsRes, tasksRes, profileRes, waiverRes, licensesRes, tripTasksRes] = await Promise.all([
          api.get('/my-trips'),
          api.get('/my-tasks'),
          api.get('/profile'),
          api.get('/waiver/status'),
          api.get('/licenses').catch(() => ({ data: [] })),
          api.get('/my-trip-tasks').catch(() => ({ data: [] })),
        ]);
        setTrips(tripsRes.data);
        setTasks(tasksRes.data);
        setProfile(profileRes.data);
        setWaiverStatus(waiverRes.data);
        setLicenses(licensesRes.data);
        setTripTasks(tripTasksRes.data);
        // Check if this is a guardian session
        const meRes = await api.get('/auth/me');
        if (meRes.data?.guardian_session) {
          setIsGuardianSession(true);
          setGuardianName(meRes.data.guardian_original_name || '');
        } else {
          // Not in guardian session — check if user has dependents
          const depsRes = await api.get('/guardian/my-dependents-for-picker').catch(() => ({ data: [] }));
          if (depsRes.data && depsRes.data.length > 0) {
            setDependentNames(depsRes.data.map(d => d.name));
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // Profile completion — matches backend (no passport fields)
  const REQUIRED_PROFILE_FIELDS = [
    'first_name', 'last_name', 'phone', 'date_of_birth',
    'street_address', 'city', 'state', 'zip_code',
    'organization',
    'emergency_contact_1_name', 'emergency_contact_1_phone',
  ];
  const calculateProfileCompletion = () => {
    if (!profile) return 0;
    let done = 0;
    const total = REQUIRED_PROFILE_FIELDS.length + 2; // +email +work_roles
    if (user?.email) done++;
    if (profile.work_roles?.length > 0) done++;
    REQUIRED_PROFILE_FIELDS.forEach(f => {
      const v = profile[f];
      if (Array.isArray(v) ? v.length > 0 : (v && String(v).trim())) done++;
    });
    return Math.round((done / total) * 100);
  };

  const profileCompletion = calculateProfileCompletion();
  const passportComplete = !!(profile?.passport_surname && profile?.passport_given_names && profile?.passport_country && profile?.passport_number && profile?.passport_expiration);
  const hasTrip = trips.length > 0;

  const requiresLicense = hasTrip && profile?.work_roles?.some(r =>
    licenseRequiredRoles.some(req => r.toLowerCase() === req.toLowerCase())
  );
  const hasVerifiedLicense = licenses.some(l => l.status === 'verified');
  const hasPendingLicense = licenses.some(l => l.status === 'pending');

  const getDaysUntil = (dateStr) => {
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // Get per-trip data from tripTasks
  const perTripData = tripTasks.length > 0 ? (tripTasks[0].per_trip || []) : [];
  const upcomingPerTrip = perTripData.filter(t => new Date(t.start_date) > new Date());

  // Admin assignments — all (completed + pending)
  const pendingAssignments = tasks.filter(t => t.status !== 'completed');
  const completedAssignments = tasks.filter(t => t.status === 'completed');
  const [completingTask, setCompletingTask] = useState(null);
  const [taskNotes, setTaskNotes] = useState('');

  const handleCompleteAssignment = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'completed', completion_notes: taskNotes });
      toast.success('Assignment marked complete');
      setCompletingTask(null);
      setTaskNotes('');
      const tasksRes = await api.get('/my-tasks');
      setTasks(tasksRes.data);
    } catch { toast.error('Failed to update'); }
  };

  // Build task list for a specific trip
  const buildTripTaskList = (tripData) => {
    const t = tripData?.tasks || {};
    const yourTasks = [
      { label: 'Complete Profile', done: profileCompletion >= 100, link: '/profile', detail: profileCompletion < 100 ? `${profileCompletion}% complete` : null },
      { label: 'Complete Passport Info', done: passportComplete, link: '/profile', detail: (() => {
        if (!passportComplete) return 'Have your passport handy';
        const exp = profile?.passport_expiration;
        if (exp) {
          const expDate = new Date(exp);
          const sixMonths = new Date(); sixMonths.setMonth(sixMonths.getMonth() + 6);
          if (expDate < new Date()) return 'Passport expired — please renew';
          if (expDate < sixMonths) return `Passport expires ${expDate.toLocaleDateString()} — renew before travel`;
        }
        return null;
      })(), warning: passportComplete && profile?.passport_expiration && new Date(profile.passport_expiration) < new Date(new Date().setMonth(new Date().getMonth() + 6)) },
      { label: 'Sign Liability Waiver', done: !!t.waiver_signed, link: '/waiver', warning: !!t.waiver_partial },
      ...(requiresLicense ? [{ label: 'Upload License / Credentials', done: hasVerifiedLicense, link: '/licenses', detail: hasPendingLicense ? 'Under review' : null }] : []),
      { label: 'Submit Flight Info', done: !!t.flight_submitted, link: '/flight-hotel' },
      { label: 'Submit Hotel Preferences', done: !!t.hotel_preferences, link: '/flight-hotel?tab=hotel',
        detail: !t.hotel_preferences ? `For ${tripData?.trip_name || 'this trip'}` : null },
      { label: 'Confirm Donation',
        done: !!t.donation_confirmed,
        selfReported: !!t.donation_self_reported,
        isDonation: true,
        tripId: tripData?.trip_id,
        detail: t.donation_confirmed ? 'Confirmed' : t.donation_self_reported ? 'Pending admin confirmation' : null },
    ];

    const adminTasks = [
      { label: 'Room Assignment', done: !!t.room_assigned,
        detail: t.room_info
          ? `${t.room_info.room_name}${t.room_info.roommates?.length ? ` with ${t.room_info.roommates.join(', ')}` : ''}`
          : 'Pending — assigned by trip coordinator' },
      { label: 'Supply Bag', done: !!t.bag_assigned,
        bagColor: (() => {
          if (!t.bag_info) return 'grey';
          if (t.bag_info.bag_number === 'None') return 'grey';
          if (['arrived', 'unpacked'].includes(t.bag_info.bag_status)) return 'green';
          if (t.bag_info.bag_status === 'in_transit') return 'orange';
          return 'yellow';
        })(),
        detail: t.bag_info
          ? t.bag_info.bag_number === 'None' ? 'No bag assigned'
            : t.bag_info.bag_status === 'in_transit'
              ? `In Transit — Bag${t.bag_info.bag_count > 1 ? 's' : ''} ${t.bag_info.bag_count > 1 ? t.bag_info.bag_number : '#' + t.bag_info.bag_number}`
              : ['arrived', 'unpacked'].includes(t.bag_info.bag_status)
                ? `Bag${t.bag_info.bag_count > 1 ? 's' : ''} ${t.bag_info.bag_count > 1 ? t.bag_info.bag_number : '#' + t.bag_info.bag_number} — Arrived`
                : `Bag${t.bag_info.bag_count > 1 ? 's' : ''} ${t.bag_info.bag_count > 1 ? t.bag_info.bag_number : '#' + t.bag_info.bag_number}`
          : 'Pending — assigned by trip coordinator' },
    ];

    return { yourTasks, adminTasks };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }



  const handleSwitchBack = async () => {
    try {
      const res = await api.post('/guardian/switch-back');
      if (res.data.token) {
        setSessionToken(res.data.token);
      }
      toast.success('Switched back to your account');
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error('Failed to switch back');
    }
  };


  const dashActions = (
    <>
      {isAdmin && <Badge className="bg-primary/10 text-primary border-0">Admin</Badge>}
      <Link to="/ask">
        <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid="ask-question-btn">
          <MessageCircle className="w-3.5 h-3.5 mr-1.5" />Ask a Question
        </Button>
      </Link>
    </>
  );

  // Render task item
  const renderTaskItem = (task, i, tripId) => {
    const isExpanded = expandedTask === `${tripId}_${i}`;
    const donationSelfReported = task.selfReported && !task.done;
    const bgClass = task.done && !task.warning ? 'bg-green-50' : task.warning ? 'bg-amber-50' : donationSelfReported ? 'bg-amber-50' : 'bg-amber-50 hover:bg-amber-100';
    const textColor = task.done && !task.warning ? 'text-green-800' : task.warning ? 'text-amber-800' : donationSelfReported ? 'text-amber-800' : 'text-slate-800';

    const inner = (
      <div className={`p-3 rounded-xl transition-colors ${bgClass}`}>
        <div className="flex items-center justify-between" onClick={() => task.isDonation && !task.done ? setExpandedTask(isExpanded ? null : `${tripId}_${i}`) : null}>
          <div className="flex items-center gap-3">
            {task.done && !task.warning ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              : task.warning ? <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              : <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />}
            <div>
              <p className={`font-medium text-sm ${textColor}`}>{task.label}</p>
              {task.detail && <p className={`text-xs ${task.warning ? 'text-amber-600' : donationSelfReported ? 'text-amber-600' : 'text-slate-500'}`}>{task.detail}</p>}
            </div>
          </div>
          {(!task.done || task.warning) && !task.isDonation && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
          {task.isDonation && !task.done && <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />}
        </div>
        {task.isDonation && isExpanded && !task.done && (
          <div className="mt-3 pt-3 border-t border-amber-200 space-y-3">
            {donationNote && <p className="text-sm text-slate-700 leading-relaxed">{donationNote}</p>}
            <div className="flex flex-col sm:flex-row gap-2">
              <a href={donationUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button className="w-full rounded-full bg-rojo hover:bg-rojo-dark h-10" size="sm">Donate Now</Button>
              </a>
              {!donationSelfReported && (
                <Button variant="outline" className="flex-1 rounded-full h-10" size="sm" disabled={reportingDonation}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setReportingDonation(true);
                    try {
                      if (task.tripId) {
                        await api.post(`/trips/${task.tripId}/self-report-donation`);
                        toast.success('Thank you! Admin will confirm receipt.');
                        setExpandedTask(null);
                        const ttRes = await api.get('/my-trip-tasks');
                        setTripTasks(ttRes.data);
                      }
                    } catch { toast.error('Failed to report'); }
                    finally { setReportingDonation(false); }
                  }}>
                  I've Completed My Donation
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
    if (task.done || task.isDonation) return <div key={i}>{inner}</div>;
    return <Link key={i} to={task.link}>{inner}</Link>;
  };

  // Render coordinator-assigned items
  const renderAdminTask = (task, i) => {
    const bgClass = task.bagColor === 'green' ? 'bg-green-50' : task.bagColor === 'orange' ? 'bg-orange-50' : task.bagColor === 'yellow' ? 'bg-yellow-50' : task.done ? 'bg-green-50' : 'bg-slate-50';
    const iconColor = task.bagColor === 'green' ? 'text-green-500' : task.bagColor === 'orange' ? 'text-orange-500' : task.bagColor === 'yellow' ? 'text-yellow-500' : task.done ? 'text-green-500' : 'text-slate-400';
    const textColor = task.bagColor === 'green' ? 'text-green-800' : task.bagColor === 'orange' ? 'text-orange-800' : task.bagColor === 'yellow' ? 'text-yellow-800' : task.done ? 'text-green-800' : 'text-slate-600';
    return (
      <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${bgClass}`}>
        {task.bagColor === 'orange' ? <AlertCircle className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
          : task.done || task.bagColor === 'yellow' ? <CheckCircle className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
          : <Clock className="w-5 h-5 text-slate-400 flex-shrink-0" />}
        <div>
          <p className={`font-medium text-sm ${textColor}`}>{task.label}</p>
          {task.detail && <p className={`text-xs ${task.bagColor === 'orange' ? 'text-orange-600' : task.bagColor === 'yellow' ? 'text-yellow-600' : 'text-slate-500'}`}>{task.detail}</p>}
        </div>
      </div>
    );
  };

  // Render a combined trip+task card
  const renderTripCard = (tripData) => {
    const { yourTasks, adminTasks } = buildTripTaskList(tripData);
    const completedCount = yourTasks.filter(t => t.done).length;
    const totalCount = yourTasks.length;
    const days = getDaysUntil(tripData.start_date);

    // Filter assignments to this trip (or global assignments with no trip_id)
    const tripPending = pendingAssignments.filter(t => !t.trip_id || t.trip_id === tripData.trip_id);
    const tripCompleted = completedAssignments.filter(t => !t.trip_id || t.trip_id === tripData.trip_id);

    return (
      <div className="space-y-4" data-testid={`trip-card-${tripData.trip_id}`}>
        {/* Trip header */}
        <div className="bg-gradient-to-r from-primary to-primary-dark rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-heading font-bold">{tripData.trip_name}</h2>
            <div className="text-right">
              <p className="text-3xl font-heading font-bold">{days}</p>
              <p className="text-xs text-white/70">days</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{tripData.start_date}</span>
            <span className="flex items-center gap-1.5"><Plane className="w-4 h-4" />{tripData.location}</span>
          </div>
          {tripData.responsibilities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {tripData.responsibilities.map(r => (
                <span key={r} className="text-xs bg-rojo/90 text-black px-2 py-0.5 rounded-full font-medium">{r}</span>
              ))}
            </div>
          )}
        </div>

        {/* Invitation acceptance banner */}
        {tripData.participant_status === 'invited' && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`accept-banner-${tripData.trip_id}`}>
            {profile?.first_name ? (
              <>
                <p className="text-sm font-medium text-amber-800 mb-2">
                  You've been invited to <strong>{tripData.trip_name}</strong> — please review your profile and confirm your information is accurate.
                </p>
                <Link to={`/profile?review=${tripData.trip_id}`}>
                  <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 h-8 text-xs">
                    Review & Accept Invitation
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-amber-800 mb-2">
                  You've been invited to <strong>{tripData.trip_name}</strong> — complete your profile to get started.
                </p>
                <Link to="/profile">
                  <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 h-8 text-xs">
                    Complete My Profile
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}

        {/* Task progress */}
        <div className="flex items-center gap-3">
          <Progress value={(completedCount / totalCount) * 100} className="h-2 flex-1" />
          <span className="text-sm font-medium text-slate-600 whitespace-nowrap">{completedCount}/{totalCount}</span>
        </div>
        {completedCount < totalCount && (
          <p className="text-xs text-slate-400 -mt-1">Tap a task to get started</p>
        )}

        {/* Your tasks */}
        <div className="space-y-1.5">
          {yourTasks.map((task, i) => renderTaskItem(task, i, tripData.trip_id))}
        </div>

        {/* Coordinator tasks */}
        {adminTasks.length > 0 && (
          <>
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-slate-400">Assigned by your trip coordinator</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {adminTasks.map((task, i) => renderAdminTask(task, i))}
            </div>
          </>
        )}

        {/* Admin assignments — with completion checkbox */}
        {(tripPending.length > 0 || tripCompleted.length > 0) && (
          <div className="space-y-1.5 pt-2">
            {tripPending.map((task) => (
              <div key={task.task_id} className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => setCompletingTask(completingTask === task.task_id ? null : task.task_id)}
                    className="w-5 h-5 rounded border-2 border-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5 hover:bg-amber-100 transition-colors"
                    data-testid={`complete-assignment-${task.task_id}`}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-800 text-sm">{task.title}</p>
                    {task.due_date && <p className="text-xs text-slate-500">Due: {task.due_date}</p>}
                    {task.description && <p className="text-xs text-slate-500 mt-1">{task.description}</p>}
                  </div>
                </div>
                {completingTask === task.task_id && (
                  <div className="mt-3 pt-3 border-t border-amber-200 space-y-2">
                    <Input
                      value={taskNotes}
                      onChange={(e) => setTaskNotes(e.target.value)}
                      placeholder="Add notes (optional)..."
                      className="text-sm h-9"
                      data-testid={`assignment-notes-${task.task_id}`}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-8 text-xs rounded-full bg-green-600 hover:bg-green-700"
                        onClick={() => handleCompleteAssignment(task.task_id)}
                        data-testid={`confirm-complete-${task.task_id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Mark Complete
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setCompletingTask(null); setTaskNotes(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {tripCompleted.map((task) => (
              <div key={task.task_id} className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800 text-sm line-through">{task.title}</p>
                  {task.completion_notes && <p className="text-xs text-green-600">{task.completion_notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View trip details link */}
        <Link to={`/trips/${tripData.trip_id}`}>
          <Button variant="outline" className="w-full rounded-full mt-2">
            View Trip Details <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    );
  };

  return (
    <PageLayout title={`Welcome back, ${user?.name?.split(' ')[0]}!`} headerActions={dashActions}>
        <div data-testid="dashboard-page">
          {/* Alert cards */}
          <div className="space-y-4 mb-8">
            {!waiverStatus.signed && (
              <Card className="border-status-warning/30 bg-status-warning/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-status-warning" />
                      <div>
                        <p className="font-medium text-slate-800">Waiver Not Signed</p>
                        <p className="text-sm text-slate-500">Please sign the liability waiver to participate.</p>
                      </div>
                    </div>
                    <Link to="/waiver">
                      <Button size="sm" className="rounded-full bg-status-warning hover:bg-status-warning/90">Sign Now</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {profileCompletion < 100 && (
              <Card className="border-status-info/30 bg-status-info/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-status-info" />
                      <div>
                        <p className="font-medium text-slate-800">Complete Your Profile</p>
                        <p className="text-sm text-slate-500">{profileCompletion}% complete</p>
                      </div>
                    </div>
                    <Link to="/profile">
                      <Button size="sm" variant="outline" className="rounded-full border-status-info text-status-info hover:bg-status-info/5">Complete</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {requiresLicense && !hasVerifiedLicense && (
              <Card className={`border-primary/30 ${hasPendingLicense ? 'bg-blue-50' : 'bg-primary/5'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertCircle className={`w-5 h-5 ${hasPendingLicense ? 'text-blue-500' : 'text-primary'}`} />
                      <div>
                        <p className="font-medium text-slate-800">{hasPendingLicense ? 'License Under Review' : 'License Upload Required'}</p>
                        <p className="text-sm text-slate-500">{hasPendingLicense ? 'Your credential is pending admin verification.' : 'As a surgeon/anesthesiologist, please upload your medical license.'}</p>
                      </div>
                    </div>
                    <Link to="/licenses">
                      <Button size="sm" className="rounded-full" variant={hasPendingLicense ? 'outline' : 'default'}>{hasPendingLicense ? 'View Status' : 'Upload Now'}</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Guardian info card — shown on guardian's own dashboard */}
            {!isGuardianSession && dependentNames.length > 0 && (
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-purple-800">Guardian</p>
                      <p className="text-sm text-purple-600">You are the guardian for {dependentNames.join(', ')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Trip + Task Cards */}
          {upcomingPerTrip.length > 0 ? (
            <>
              {/* Mobile: tabs */}
              {upcomingPerTrip.length > 1 && (
                <div className="flex gap-1 mb-4 lg:hidden bg-slate-100 rounded-xl p-1" data-testid="trip-tabs-mobile">
                  {upcomingPerTrip.map((t, idx) => (
                    <button
                      key={t.trip_id}
                      onClick={() => setSelectedTripIdx(idx)}
                      className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedTripIdx === idx
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`trip-tab-${idx}`}
                    >
                      {t.trip_name?.replace(/October \d{4} - /, '') || `Trip ${idx + 1}`}
                    </button>
                  ))}
                </div>
              )}

              {/* Mobile: show selected trip only */}
              <div className="lg:hidden">
                <Card>
                  <CardContent className="p-4">
                    {renderTripCard(upcomingPerTrip[selectedTripIdx] || upcomingPerTrip[0])}
                  </CardContent>
                </Card>
              </div>

              {/* Desktop: side by side */}
              <div className="hidden lg:grid lg:grid-cols-2 gap-6">
                {upcomingPerTrip.map(tripData => (
                  <Card key={tripData.trip_id}>
                    <CardContent className="p-5">
                      {renderTripCard(tripData)}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card className="bg-slate-50 border-dashed">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-600 mb-2">No Upcoming Trips</h3>
                <p className="text-sm text-slate-500 mb-4">You're not registered for any upcoming missions yet.</p>
                <Link to="/#trips">
                  <Button variant="outline" className="rounded-full">Browse Available Trips</Button>
                </Link>
              </CardContent>
            </Card>
          )}

        </div>
    </PageLayout>
  );
};

export default Dashboard;
